import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { Logo } from './Logo'; // 기존 PickFlip 로고는 사용하지 않으므로 주석 처리 또는 삭제
import LanguageSwitcher from './LanguageSwitcher';
import LogoutModal from './LogoutModal';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isHomePage = location.pathname === '/';
  const user = auth.currentUser;
  
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // 슬라이더 상태 추가

  const handleCreateEvent = () => {
    if (isHomePage) {
      const element = document.getElementById('create-event-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
      alert(t('alerts.logoutFailed'));
    }
  };

  return (
    <>
      {/* 1. 상단 메인 헤더 */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderBottom: '3px solid #2C3E50',
        marginBottom: '6px',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '12px 15px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* 햄버거 메뉴 버튼 + PickFlip 로고 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button
              onClick={() => setIsDrawerOpen(true)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#2C3E50',
                padding: '0',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              ☰
            </button>
            
            <div 
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              onClick={() => navigate('/')}
            >
             <Logo size="md" />
            </div>
          </div>
          
          {/* Create Your Own Event 버튼 (메인에 유지) */}
          <button
            onClick={handleCreateEvent}
            style={{
              backgroundColor: '#FFD93D',
              border: '2px solid #2C3E50',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontWeight: '800',
              fontSize: '14px',
              color: '#2C3E50',
              boxShadow: '2px 2px 0px #2C3E50',
              transition: 'all 0.2s',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
              e.currentTarget.style.boxShadow = '4px 4px 0px #2C3E50';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
              e.currentTarget.style.boxShadow = '2px 2px 0px #2C3E50';
            }}
          >
            {t('home.createEvent')}
          </button>
        </div>
      </div>

      {/* 2. 슬라이더(Drawer) 배경 오버레이 */}
      {isDrawerOpen && (
        <div 
          onClick={() => setIsDrawerOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(44, 62, 80, 0.6)',
            zIndex: 999,
            backdropFilter: 'blur(3px)' // 뒷배경 흐리게
          }}
        />
      )}

      {/* 3. 좌측 슬라이더 메뉴 패널 */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: isDrawerOpen ? '0' : '-300px', // 상태에 따라 열리고 닫힘
        width: '280px',
        height: '100vh',
        backgroundColor: '#FFFFFF',
        zIndex: 1000,
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRight: '3px solid #2C3E50',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        boxSizing: 'border-box',
        overflowY: 'auto'
      }}>
        {/* 슬라이더 헤더 (닫기 버튼) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
          <button 
            onClick={() => setIsDrawerOpen(false)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2C3E50',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>

        {/* 사용자 정보 */}
        {user && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px',
            paddingBottom: '30px',
            borderBottom: '2px dashed #BDC3C7',
            marginBottom: '30px'
          }}>
            <img 
              src={user.photoURL || 'https://via.placeholder.com/64'} 
              alt={user.displayName || 'User'}
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                border: '3px solid #2C3E50'
              }}
            />
            <span style={{ fontSize: '18px', fontWeight: '800', color: '#2C3E50' }}>
              {user.displayName || 'User'}
            </span>
          </div>
        )}

        {/* 슬라이더 메뉴 아이템들 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* 언어 설정 */}
          <div>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#7F8C8D' }}>{t('header.language')}</p>
            <LanguageSwitcher />
          </div>
        </div>

        {/* 하단 로그아웃 버튼 */}
        <div style={{ marginTop: 'auto' }}>
          <button
            onClick={() => {
              setIsDrawerOpen(false);
              setShowLogoutModal(true);
            }}
            style={{
              width: '100%',
              backgroundColor: '#FF6B9D',
              border: '2px solid #2C3E50',
              borderRadius: '8px',
              padding: '12px',
              cursor: 'pointer',
              fontWeight: '800',
              fontSize: '16px',
              color: '#FFFFFF',
              boxShadow: '3px 3px 0px #2C3E50',
              transition: 'all 0.2s',
              fontFamily: 'inherit'
            }}
          >
            {t('header.logout')}
          </button>
        </div>
      </div>

      {/* 로그아웃 모달 */}
      <LogoutModal
        isOpen={showLogoutModal}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutModal(false)}
      />
    </>
  );
}

export default Header;